import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  Schedule,
  Send,
  CheckCircle,
} from '@mui/icons-material';
import TableOrderLayout from '../../components/user/TableOrderLayout';
import { useNotification } from '../../contexts/NotificationContext';
import api from '../../services/api';

const ContactPage = () => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: 'general'
  });

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'reservation', label: 'Table Reservation' },
    { value: 'catering', label: 'Catering Services' },
    { value: 'complaint', label: 'Complaint' },
    { value: 'compliment', label: 'Compliment' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      showError('Please fill in all required fields');
      return;
    }

    if (!formData.email.includes('@')) {
      showError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      await api.post('/public/contact', formData);
      setSubmitted(true);
      showSuccess('Thank you for your message! We will get back to you soon.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        inquiryType: 'general'
      });
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      showError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <LocationOn />,
      title: "Address",
      details: "123 Main Street, Downtown\nCity, State 12345"
    },
    {
      icon: <Phone />,
      title: "Phone",
      details: "+1 (555) 123-4567\n+1 (555) 987-6543"
    },
    {
      icon: <Email />,
      title: "Email",
      details: "info@restaurant.com\nreservations@restaurant.com"
    },
    {
      icon: <Schedule />,
      title: "Hours",
      details: "Mon-Fri: 11:00 AM - 10:00 PM\nSat-Sun: 10:00 AM - 11:00 PM"
    }
  ];

  if (submitted) {
    return (
      <TableOrderLayout 
        title="Contact Us" 
        subtitle="We'd love to hear from you"
        showBackButton={true}
        showCart={false}
      >
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom color="success.main">
              Message Sent Successfully!
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Thank you for contacting us. We have received your message and will get back to you within 24 hours.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => setSubmitted(false)}
              sx={{ mt: 2 }}
            >
              Send Another Message
            </Button>
          </Paper>
        </Container>
      </TableOrderLayout>
    );
  }

  return (
    <TableOrderLayout 
      title="Contact Us" 
      subtitle="We'd love to hear from you"
      showBackButton={true}
      showCart={false}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Typography 
              variant="h5" 
              fontWeight="bold" 
              gutterBottom
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
            >
              Get in Touch
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              paragraph
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              We're here to help and answer any question you might have. 
              We look forward to hearing from you!
            </Typography>

            {contactInfo.map((info, index) => (
              <Card key={index} elevation={1} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                      {info.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {info.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {info.details}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Typography 
                variant="h5" 
                fontWeight="bold" 
                gutterBottom
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
              >
                Send us a Message
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                paragraph
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Fill out the form below and we'll get back to you as soon as possible.
              </Typography>
              <Divider sx={{ mb: { xs: 2, sm: 3 } }} />

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name *"
                      value={formData.name}
                      onChange={handleInputChange('name')}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address *"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange('phone')}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Inquiry Type</InputLabel>
                      <Select
                        value={formData.inquiryType}
                        onChange={handleInputChange('inquiryType')}
                        label="Inquiry Type"
                      >
                        {inquiryTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      value={formData.subject}
                      onChange={handleInputChange('subject')}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message *"
                      multiline
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange('message')}
                      required
                      variant="outlined"
                      placeholder="Please describe your inquiry in detail..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                      sx={{ minWidth: 150 }}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Additional Information */}
        <Paper elevation={1} sx={{ p: 3, mt: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Frequently Asked Questions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                How do I make a reservation?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                You can make a reservation through our website, call us directly, or visit us in person. 
                We recommend booking in advance, especially for weekends and special occasions.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Do you offer catering services?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Yes! We provide full catering services for events, parties, and corporate functions. 
                Contact us for custom menu options and pricing.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                What are your delivery hours?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                We deliver Monday through Sunday from 11:00 AM to 10:00 PM. 
                Delivery times may vary based on location and order volume.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Do you accommodate dietary restrictions?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Absolutely! We offer vegetarian, vegan, and gluten-free options. 
                Please inform us of any dietary restrictions when placing your order.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </TableOrderLayout>
  );
};

export default ContactPage;
